import { Box, Flex, Text } from '@modusign/design-system';
import pFileReader from 'promise-file-reader';
import { createRef, MouseEvent, useState } from 'react';
import DropZone, { DropzoneRef } from 'react-dropzone';

import ImageIcon from '../ImageIcon';
import ImageUploadErrorModal, { FileSelectError } from '../ImageUploadErrorModal';

export const ACCEPT_FILETYPE = ['.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 1024 * 1024 * 3; // 3MB 까지 허용

interface ImageDropZoneProps {
  changeable: boolean;
  disabled: boolean;
  hidePlaceholderText: boolean;
  onBlur: () => void;
  onChange: (image: { imageSrc: string; fileName: string }) => void;
  onClick: () => void;
  onFocus: () => void;
}

function ImageDropZone({
  changeable,
  disabled,
  hidePlaceholderText,
  onBlur,
  onChange,
  onClick,
  onFocus,
}: ImageDropZoneProps) {
  const dropzoneRef = createRef<DropzoneRef>();

  const [errors, setErrors] = useState<FileSelectError>([]);

  function handleClickFile(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current && changeable && !disabled) {
      dropzoneRef.current.open();
    } else {
      onClick();
    }
  }

  async function loadImage(file: File) {
    try {
      const imageSrc = await pFileReader.readAsDataURL(file);
      onChange({ imageSrc, fileName: file.name });
    } catch {
      setErrors([...errors, 'fileOpenError']);
    }
  }

  async function handleOnDrop(acceptFile: File[]) {
    const fileSelectError: FileSelectError = [];
    if (!acceptFile[0]) {
      fileSelectError.push('fileNotSelected');
      return;
    }
    const parsing = acceptFile[0].name.split('.');
    const extension = parsing.pop()?.toLowerCase() ?? '';

    if (!ACCEPT_FILETYPE.includes(`.${extension}`)) {
      fileSelectError.push('extension');
    }
    if (acceptFile[0].size > MAX_FILE_SIZE) {
      fileSelectError.push('fileSize');
    }

    setErrors(fileSelectError);
    if (fileSelectError.length === 0) {
      await loadImage(acceptFile[0]);
    }
  }

  return (
    <>
      <DropZone
        accept={{
          'image/*': ACCEPT_FILETYPE,
        }}
        maxFiles={1}
        multiple={false}
        noClick
        ref={dropzoneRef}
        useFsAccessApi={false}
        onDrop={handleOnDrop}
      >
        {({ getRootProps, getInputProps }) => (
          <Box className="UniversalImageField__box" {...getRootProps()} tabIndex={-1}>
            <input {...getInputProps()} tabIndex={-1} />
            <Box
              as="button"
              height="100%"
              tabIndex={0}
              type="button"
              width="100%"
              onBlur={onBlur}
              onClick={handleClickFile}
              onFocus={onFocus}
            >
              <Flex alignItems="center" flexDirection="column" justifyContent="center">
                <ImageIcon />
                {!hidePlaceholderText && (
                  <Text color="textNeutralLight" p={2}>
                    여기를 클릭하여
                    <br />
                    JPG, PNG 이미지를
                    <br />
                    삽입해 주세요.
                  </Text>
                )}
              </Flex>
            </Box>
          </Box>
        )}
      </DropZone>
      <ImageUploadErrorModal
        errors={errors}
        isOpen={errors.length > 0}
        onConfirm={() => setErrors([])}
      />
    </>
  );
}

export default ImageDropZone;
